import React from 'react';
import { Box } from '@mui/material';
import { useTranslation } from '@askporter/config-provider';
import { AlertBox } from '../AlertBox';
import { AlertBoxMessage } from '../AlertBox/AlertBoxMessage';
import { AlertBoxTitle } from '../AlertBox/AlertBoxTitle';
import { useMaintenanceWarning } from './useMaintenanceWarning';

export { useMaintenanceWarning, WARNING_LEAD_MINUTES, DEFAULT_WARNING_LEAD_MINUTES } from './useMaintenanceWarning';
export type { MaintenanceWarning } from './useMaintenanceWarning';

/**
 * English copy of last resort, used only where the dictionary has no entry.
 *
 * `defaultValue` rather than `t(key) || 'literal'` for the reason documented on
 * `useMaintenanceCopy`: this app sets `keySeparator: false` with no
 * `fallbackLng`, so a missing key resolves to the key itself, which is truthy —
 * an `||` fallback never fires and the raw key reaches the screen.
 */
const EN = {
  title: 'Maintenance starting soon',
  message:
    'Scheduled maintenance starts at {{startsAt}}, in about {{minutesUntil}} minutes. Saving changes will be unavailable while it runs, so please finish anything in progress.',
};

export interface MaintenanceWarningBannerProps {
  /** Optional margin applied to the underlying AlertBox. */
  margin?: string;
}

/**
 * Banner shown in the run-up to a scheduled maintenance window, so a user can
 * finish or save what they are doing rather than losing it (NS-2465).
 *
 * This is the third of three maintenance signals and they must not look alike:
 *
 * | Signal | Component | Tone | Means |
 * | --- | --- | --- | --- |
 * | Degradation, everything still working | `DegradedServiceBanner` | `warning` | COM-112, unplanned |
 * | A window is open, writes refused | `MaintenanceBanner` | `info` | NS-2461, in progress |
 * | A window is coming | this | `brand` | NS-2465, not yet started |
 *
 * `brand` is the remaining tone that reads as noticeable without being
 * alarming — `error` overstates a planned window, and `info` and `warning` are
 * both taken. Per the 17 Aug decision this reuses existing banner presentation
 * rather than blocking on Figma, so the tone is provisional.
 *
 * Renders nothing unless a window is genuinely approaching; every unhappy path
 * is decided in {@link useMaintenanceWarning}.
 *
 * Accessibility: `AlertBox` carries `role="alert"`, so the warning is announced
 * to a screen-reader user rather than only appearing visually.
 */
export const MaintenanceWarningBanner: React.FC<MaintenanceWarningBannerProps> = ({ margin = '8px 8px 0' }) => {
  const { isWarning, startsAt, minutesUntil } = useMaintenanceWarning();
  const { t } = useTranslation();

  if (!isWarning) return null;

  return (
    <Box data-testid="maintenance-warning-banner">
      <AlertBox alertType="brand" alertBoxMargin={margin}>
        <AlertBoxTitle disableMarginTop>
          {t('ns.maintenance_mode:upcoming_title', { defaultValue: EN.title })}
        </AlertBoxTitle>
        <AlertBoxMessage>
          {t('ns.maintenance_mode:upcoming_message', {
            defaultValue: EN.message,
            startsAt,
            minutesUntil,
          })}
        </AlertBoxMessage>
      </AlertBox>
    </Box>
  );
};
